import { useFormSchema } from "@portal/providers/FormSchemaProvider";
import { type ReactNode } from "react";
import { useFormContext } from "react-hook-form";
import { Link, useSearchParams } from "react-router-dom";
import {
	getNestedValue,
	isEmpty,
	isFieldPartial,
	isFieldRequiredForApproval,
} from "../../../utils/schema-fields";
import { cn } from "../../lib/utils";
import { PartialIndicator } from "../../primitives";
import { RequiredForApprovalIndicator } from "../../ui/RequiredForApprovalIndicator";
import { getNestedError } from "../utils/formResolver";
import { buildSummaryLink } from "../utils/summary.utils";

export type SummaryFieldProps = {
	label?: string;
	value?: string | "" | (string | "")[] | ReactNode;
	fieldPath?: string;
	to?: { pathname: string; section: string; id?: string };
	partial?: boolean;
	vertical?: true;
	testId?: string;
};

const SummaryFieldValue = ({ value }: { value: string | ReactNode | undefined }) => (
	<div className="font-normal text-[14px]">{value ?? "--"}</div>
);

const SummaryFieldContent = ({
	label,
	value,
	partial,
	requiredForApproval,
	error,
	vertical,
	testId,
}: {
	label?: string;
	value?: string | "" | (string | "")[] | ReactNode;
	partial: boolean;
	requiredForApproval: boolean;
	error: boolean;
	vertical?: true;
	testId?: string;
}) => {
	const _value = (() => {
		if (Array.isArray(value)) {
			const filtered = value.filter((val) => val !== "" && val !== undefined);
			if (!filtered.length) return undefined;
			return vertical ? filtered : filtered.join(", ");
		}
		if (value === "") return undefined;
		return value;
	})();

	return (
		<div
			data-testid={testId}
			className={cn(
				"group flex w-fit text-[13px] text-primary last:!mb-0 cursor-pointer",
				vertical ? "flex-col space-y-[5px] pb-[10px]" : "items-center space-x-[8px]",
				error ? "text-red-500" : undefined,
			)}
		>
			{label !== undefined && (
				<div className="flex items-start space-x-[8px]">
					<div className="font-semibold group-hover:underline">{label}</div>
					{partial && <PartialIndicator active={true} className="shrink-0" />}
					{requiredForApproval && (
						<RequiredForApprovalIndicator active={true} className="shrink-0" />
					)}
				</div>
			)}
			<div className={cn("font-normal text-[15px]", vertical ? "px-[10px]" : undefined)}>
				{Array.isArray(_value) && !_value.length && <SummaryFieldValue value={undefined} />}
				{Array.isArray(_value) && !!_value.length && (
					<div className="flex flex-col space-y-[2px]">
						{(_value as (string | "")[]).map((val, index) => (
							<SummaryFieldValue key={index} value={val} />
						))}
					</div>
				)}
				{!Array.isArray(_value) && <SummaryFieldValue value={_value} />}
			</div>
		</div>
	);
};

export const SummaryField = ({
	to,
	label,
	value,
	fieldPath,
	partial: partialOverride,
	vertical,
	testId,
}: SummaryFieldProps) => {
	const schema = useFormSchema();
	const { getValues, formState } = useFormContext();
	const [searchParams] = useSearchParams();

	const isSubmitted = formState.isSubmitted;
	const isValidated = isSubmitted || Object.keys(formState.errors).length > 0;

	const partial =
		partialOverride ??
		(fieldPath && schema && isSubmitted
			? isFieldPartial(schema, fieldPath) && isEmpty(getNestedValue(getValues(), fieldPath))
			: false);

	const requiredForApproval =
		!!fieldPath &&
		!!schema &&
		isSubmitted &&
		isFieldRequiredForApproval(schema, fieldPath) &&
		isEmpty(getNestedValue(getValues(), fieldPath));

	const fieldError = fieldPath ? getNestedError(formState.errors, fieldPath) : undefined;
	const hasError =
		isValidated &&
		!!fieldError &&
		typeof fieldError === "object" &&
		(fieldError as Record<string, unknown>).type === "error";

	const contentProps = {
		label,
		value,
		partial,
		requiredForApproval,
		error: hasError,
		vertical,
		testId,
	};

	return to !== undefined ? (
		<Link to={buildSummaryLink(to, searchParams)}>
			<SummaryFieldContent {...contentProps} />
		</Link>
	) : (
		<SummaryFieldContent {...contentProps} />
	);
};
